<template>
  <button
    :type="type"
    class="ui-btn"
    :class="[
      `ui-btn--${variant}`,
      `ui-btn--${size}`,
      { 'ui-btn--block': block, 'ui-btn--loading': loading }
    ]"
    :disabled="disabled || loading"
    :aria-disabled="loading ? 'true' : undefined"
    :aria-busy="loading ? 'true' : undefined"
    @click="handleClick"
  >
    <span v-if="loading" class="ui-btn__spinner" aria-hidden="true"></span>
    <template v-else>
      <span v-if="hasIconLeft" class="ui-btn__icon ui-btn__icon--left">
        <slot name="icon-left"></slot>
      </span>
      <slot></slot>
      <span v-if="hasIconRight" class="ui-btn__icon ui-btn__icon--right">
        <slot name="icon-right"></slot>
      </span>
    </template>
  </button>
</template>

<script>
export default {
  name: 'UiButton',
  props: {
    variant: {
      type: String,
      default: 'primary',
      validator: v => ['primary', 'white', 'secondary', 'ghost', 'outline', 'danger'].includes(v),
    },
    size: {
      type: String,
      default: 'md',
      validator: v => ['sm', 'md', 'lg', 'xl'].includes(v),
    },
    loading: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    type: { type: String, default: 'button' },
    block: { type: Boolean, default: false },
  },
  computed: {
    hasIconLeft() {
      return !!this.$slots['icon-left'];
    },
    hasIconRight() {
      return !!this.$slots['icon-right'];
    },
  },
  methods: {
    handleClick(e) {
      if (!this.disabled && !this.loading) {
        this.$emit('click', e);
      }
    },
  },
};
</script>

<style scoped lang="scss">
.ui-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: 1px solid transparent;
  border-radius: var(--radius-sm);
  font-family: var(--font-sans);
  font-weight: var(--weight-semibold);
  letter-spacing: 0.1px;
  cursor: pointer;
  transition: background var(--duration-fast) var(--easing-standard),
              box-shadow var(--duration-fast) var(--easing-standard),
              opacity var(--duration-fast) var(--easing-standard);
  white-space: nowrap;
  position: relative;
  text-decoration: none;
  -webkit-user-select: none;
  user-select: none;
}

.ui-btn--block {
  width: 100%;
}

/* ── Sizes ── */
.ui-btn--sm { height: 32px; padding-inline: 14px; font-size: 13px; gap: 6px; }
.ui-btn--md { height: 40px; padding-inline: 20px; font-size: 14px; gap: 8px; }
.ui-btn--lg { height: 48px; padding-inline: 26px; font-size: 16px; gap: 10px; }
.ui-btn--xl { height: 56px; padding-inline: 32px; font-size: 17px; gap: 12px; }

/* ── Variants ── */
.ui-btn--primary {
  background: var(--color-accent);
  color: #ffffff;
  border-color: transparent;
}
.ui-btn--primary:hover:not(:disabled) {
  background: var(--color-accentHover);
}
.ui-btn--primary:active:not(:disabled) {
  background: var(--color-accentPress);
}

.ui-btn--white {
  background: #ffffff;
  color: #000000;
  border-color: transparent;
}
.ui-btn--white:hover:not(:disabled) {
  opacity: 0.85;
}

.ui-btn--secondary {
  background: rgba(255, 255, 255, 0.12);
  color: var(--color-text);
  border-color: transparent;
}
.ui-btn--secondary:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.18);
}

.ui-btn--ghost {
  background: transparent;
  color: var(--color-text);
  border-color: transparent;
}
.ui-btn--ghost:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.08);
}

.ui-btn--outline {
  background: transparent;
  color: var(--color-text);
  border-color: var(--color-borderHi);
}
.ui-btn--outline:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.06);
}

.ui-btn--danger {
  background: var(--color-danger);
  color: #ffffff;
  border-color: transparent;
}
.ui-btn--danger:hover:not(:disabled) {
  opacity: 0.85;
}

/* ── States ── */
.ui-btn:focus-visible {
  outline: none;
  box-shadow: var(--shadow-glow);
}

.ui-btn:disabled,
.ui-btn--loading {
  opacity: 0.4;
  cursor: not-allowed;
}

/* ── Spinner ── */
.ui-btn__spinner {
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-top-color: #ffffff;
  border-radius: 50%;
  animation: ui-btn-spin var(--duration-spinner) var(--easing-linear) infinite;
}

.ui-btn--white .ui-btn__spinner {
  border-color: rgba(0, 0, 0, 0.3);
  border-top-color: #000000;
}

.ui-btn__icon {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
}

/* ── Reduced motion ── */
@media (prefers-reduced-motion: reduce) {
  .ui-btn {
    transition: none;
  }
  .ui-btn__spinner {
    animation: none;
    opacity: 0.5;
  }
}

@keyframes ui-btn-spin {
  to { transform: rotate(360deg); }
}
</style>
